import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useVersionCheck — app-wide guard against a stale frontend.
 *
 * Long-lived SPA tabs keep running whatever JS they booted with: index.html is
 * served `no-cache` and bundles are content-hashed, so a *fresh load* always
 * gets the latest build — but a tab a clinician left open for days never
 * reloads on its own. That is exactly how PHA-866 (hide target dates for ODA)
 * appeared "broken" for one user while working for everyone else: her tab was
 * running a pre-deploy bundle.
 *
 * This hook detects *any* new deploy (not just one feature) by polling the CRA
 * build manifest (`/asset-manifest.json`, served same-origin, `no-cache`) and
 * comparing the entry bundle hash against the one present when this tab booted.
 * When they differ, a newer build has been deployed than the one running here.
 *
 * Design guarantees:
 * - Baseline is captured once, from the same URL we poll (running == deployed
 *   at load time, so it is a correct baseline).
 * - A failed/errored fetch is a strict no-op — never a false "update available"
 *   (prevents a transient network blip from popping a spurious reload banner).
 * - Polls on an interval AND when the tab regains visibility/focus — the
 *   highest-yield trigger for the "tab open for days" case is the user coming
 *   back to it.
 * - Stops polling once an update is detected; the value only latches on.
 */

const MANIFEST_URL = '/asset-manifest.json';
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
// Don't hammer the manifest on rapid focus/visibility flips.
const MIN_CHECK_SPACING_MS = 30 * 1000;

interface AssetManifest {
  files?: Record<string, string>;
  entrypoints?: string[];
}

/**
 * A stable signature of the currently-deployed build. We prefer the full
 * entrypoints list (captures every entry chunk, JS + CSS) and fall back to the
 * main.js hash. Returns null when the manifest can't be read/parsed so callers
 * can treat it as "unknown" rather than "changed".
 */
async function fetchBuildSignature(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`${MANIFEST_URL}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type') || '';
    if (!contentType.includes('json')) return null; // SPA fallback served index.html, etc.
    const data: AssetManifest = await res.json();
    if (data?.entrypoints && Array.isArray(data.entrypoints) && data.entrypoints.length) {
      return data.entrypoints.join('|');
    }
    if (data?.files?.['main.js']) {
      return data.files['main.js'];
    }
    return null;
  } catch {
    // Network error, abort, JSON parse failure — all treated as "unknown".
    return null;
  }
}

interface UseVersionCheckOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  /** Minimum spacing between checks, to avoid hammering on focus/visibility flaps. */
  minCheckSpacingMs?: number;
}

interface UseVersionCheckReturn {
  /** True once a newer build than this tab's has been deployed. Latches on. */
  updateAvailable: boolean;
  /** Hard-reload the tab to pick up the new build. */
  reload: () => void;
}

export const useVersionCheck = ({
  enabled = true,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  minCheckSpacingMs = MIN_CHECK_SPACING_MS,
}: UseVersionCheckOptions = {}): UseVersionCheckReturn => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const baselineRef = useRef<string | null>(null);
  const lastCheckAtRef = useRef<number>(0);
  const updateAvailableRef = useRef(false);

  const check = useCallback(async () => {
    if (!enabled || updateAvailableRef.current) return;
    const now = Date.now();
    if (now - lastCheckAtRef.current < minCheckSpacingMs) return;
    lastCheckAtRef.current = now;

    const signature = await fetchBuildSignature();
    if (signature == null) return; // unknown → no-op, never a false positive

    if (baselineRef.current == null) {
      // First successful read: this is the build this tab is running.
      baselineRef.current = signature;
      return;
    }
    if (signature !== baselineRef.current) {
      updateAvailableRef.current = true;
      setUpdateAvailable(true);
    }
  }, [enabled, minCheckSpacingMs]);

  useEffect(() => {
    if (!enabled) return;

    // Capture the baseline immediately, then poll.
    check();
    const interval = window.setInterval(check, pollIntervalMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onFocus = () => check();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, pollIntervalMs, check]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, reload };
};

export default useVersionCheck;
